import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'expo-router';
import { useAuthStore, useChatInputStore } from '@/store';
import { getChatInputContextIdFromPathname } from '@/lib/chatInputContext';
import { openNewChatComposer } from '@/lib/openHomeComposer';
import { BROWSER_EXTENSION_ORIGINS, formatBrowserContext, getBrowserPanelSession, parseBrowserPanelSession, setBrowserPanelSession } from '@/lib/browserPanel';
import { browserPanelGuest, signInBrowserPanel } from '@/lib/browserPanelSignIn';
import { API_HOST, fetchWithTimeout, fetchWithAuth } from '@/lib/api/client';
import { createXReplyGenerator } from '@/lib/browserPanelXReply';
import { createBrowserWorkClient } from '@/lib/browserPanelWork';

const panelSignedIn = state => Boolean(state._hasHydrated && state.token && state.user?.id && !state.user?.flags?.isGhost && !state._isSigningOut);

export default function BrowserPanelBridge() {
  const router = useRouter();
  const pathname = usePathname();
  const userId = useAuthStore(state => state.user?.id || null);
  const guest = useAuthStore(browserPanelGuest);
  const signedIn = useAuthStore(panelSignedIn);
  const [macAvailable, setMacAvailable] = useState(false);
  const attemptedSignIn = useRef(false);
  const priorUser = useRef(userId);
  const [pending, setPending] = useState(null);
  const [connected, setConnected] = useState(false);
  const [workEnabled, setWorkEnabled] = useState(false);
  const workClient = useRef(null);
  const [extensionOrigin, setExtensionOrigin] = useState(null);
  const parentOrigin = useRef(null);
  const added = useRef(new Set());
  const [panel, setPanel] = useState(getBrowserPanelSession);
  const currentPath = useRef(pathname); currentPath.current = pathname;

  useEffect(() => {
    if (!panel && window.parent === window) return;
    const send = (type, extra = {}) => extensionOrigin && window.parent.postMessage({ channel: 'gab-browser-panel', nonce: panel.nonce, type, ...extra }, extensionOrigin);
    const xReplies = createXReplyGenerator({ getState: useAuthStore.getState, fetchWithAuth, send });
    const onMessage = event => {
      const data = event.data;
      if (event.source !== window.parent || !BROWSER_EXTENSION_ORIGINS.includes(event.origin) || (parentOrigin.current && event.origin !== parentOrigin.current) || data?.channel !== 'gab-browser-panel') return;
      if (data.type === 'hello') {
        const next = parseBrowserPanelSession({ framed: true, search: '?browserPanel=1', hash: `#gab-panel=${data.nonce}` });
        if (!next || (panel && next.nonce !== panel.nonce)) return;
        parentOrigin.current = event.origin;
        setExtensionOrigin(event.origin);
      }
      if (!parentOrigin.current) return;
      // Routers and auth redirects can remove the initial URL fragment before
      // React mounts. Only the authenticated parent may re-establish it.
      if (data.type === 'hello' && !panel) {
        const next = parseBrowserPanelSession({ framed: true, search: '?browserPanel=1', hash: `#gab-panel=${data.nonce}` });
        if (next) { setBrowserPanelSession(next); setPanel(next); }
        return;
      }
      if (!panel || data.nonce !== panel.nonce) return;
      if (data.type === 'work-enable') setWorkEnabled(data.enabled === true);
      if (data.type === 'work-result') void workClient.current?.result(data);
      if (data.type === 'mac-account-available') setMacAvailable(true);
      if (data.type === 'hello') { setConnected(true); send('ready', { path: currentPath.current, capabilities: ['x-reply-v1', 'context-actions-v1', 'sidebar-work-v1'], signedIn: panelSignedIn(useAuthStore.getState()) }); }
      if (data.type === 'x-reply') void xReplies.generate(data.request);
      if (data.type === 'x-reply-cancel') xReplies.cancel(data.id);
      if (data.type === 'new-chat') { setWorkEnabled(false); setPending(null); openNewChatComposer(router, { replace: true }); }
      if (data.type === 'context') {
        const text = formatBrowserContext(data.context);
        if (!text) { send('context-error'); return; }
        if (added.current.has(data.context.id)) { send('context-added', { id: data.context.id }); return; }
        setPending({ id: data.context.id, text });
        if (!getChatInputContextIdFromPathname(currentPath.current)) router.replace('/app');
      }
    };
    window.addEventListener('message', onMessage);
    // Parent may have sent hello before React mounted. Its response validates
    // our exact origin/window and instance nonce; credentials never cross.
    if (panel) send('ready', { path: currentPath.current, capabilities: ['x-reply-v1', 'context-actions-v1', 'sidebar-work-v1'], signedIn: panelSignedIn(useAuthStore.getState()) });
    if (!extensionOrigin) for (const origin of BROWSER_EXTENSION_ORIGINS) window.parent.postMessage({ channel: 'gab-browser-panel', type: 'listening' }, origin);
    return () => { xReplies.cancel(); window.removeEventListener('message', onMessage); };
  }, [panel, router, extensionOrigin]);

  useEffect(() => {
    if (!panel || !extensionOrigin || !workEnabled) return;
    const send = (type, extra = {}) => window.parent.postMessage({ channel: 'gab-browser-panel', nonce: panel.nonce, type, ...extra }, extensionOrigin);
    if (!signedIn) { send('work-error', { message: 'Sign in before connecting Work.' }); setWorkEnabled(false); return; }
    const conversationId = pathname.match(/^\/c\/([a-f\d]{24})$/)?.[1];
    if (!conversationId) { send('work-waiting'); return; }
    let active = true, reportedError = false;
    const user = useAuthStore.getState().user?.id;
    // The client reports its own precise failures (connect, poll, lost
    // result, delivery). Cleanup adds the generic disconnect message only
    // when the client never reported one, instead of clobbering it.
    const workSend = (type, extra = {}) => { if (type === 'work-error') reportedError = true; send(type, extra); };
    const client = createBrowserWorkClient({ request: fetchWithAuth, send: workSend, conversationId,
      current: () => active && panelSignedIn(useAuthStore.getState()) && useAuthStore.getState().user?.id === user });
    workClient.current = client; void client.start();
    return () => {
      active = false; client.stop(); if (workClient.current === client) workClient.current = null;
      if (!reportedError) send('work-error', { message: 'Work disconnected after the chat or account changed. Connect again to continue.' });
    };
  }, [panel, extensionOrigin, pathname, signedIn, userId, workEnabled]);

  useEffect(() => {
    if (!panel || !extensionOrigin || !macAvailable || !guest || attemptedSignIn.current) return;
    attemptedSignIn.current = true;
    let active = true;
    const send = (type, extra = {}) => extensionOrigin && window.parent.postMessage({ channel: 'gab-browser-panel', nonce: panel.nonce, type, ...extra }, extensionOrigin);
    const storageChanged = event => {
      if (event.key === 'auth-storage' || event.key === null) { active = false; send('mac-sign-in-complete'); }
    };
    window.addEventListener('storage', storageChanged);
    const request = async (action, body) => {
      const response = await fetchWithTimeout(`${API_HOST}/json/auth/browser/${action}`, {
        method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      }, 5000);
      if (!response.ok) throw new Error('Desktop sign-in unavailable.');
      return response.json();
    };
    void signInBrowserPanel({ getState: useAuthStore.getState, request, send, current: () => active })
      .catch(() => { if (active) send('mac-sign-in-error'); });
    return () => { active = false; window.removeEventListener('storage', storageChanged); };
  }, [panel, macAvailable, guest, extensionOrigin]);

  useEffect(() => {
    if (!panel) return;
    if (priorUser.current && priorUser.current !== userId) {
      setWorkEnabled(false);
      setPending(null); added.current.clear(); router.replace('/app');
    }
    priorUser.current = userId;
  }, [panel, userId, router]);

  useEffect(() => {
    if (!panel || !extensionOrigin) return;
    window.parent.postMessage({ channel: 'gab-browser-panel', nonce: panel.nonce, type: 'ready', path: pathname, capabilities: ['x-reply-v1', 'context-actions-v1', 'sidebar-work-v1'], signedIn: panelSignedIn(useAuthStore.getState()) }, extensionOrigin);
  }, [panel, pathname, connected, extensionOrigin, signedIn]);

  useEffect(() => {
    if (!panel || !extensionOrigin || !pending) return;
    const contextId = getChatInputContextIdFromPathname(pathname);
    if (!contextId) return;
    if (!added.current.has(pending.id)) {
      const composer = useChatInputStore.getState();
      const prior = composer.inputStates[contextId]?.text || '';
      composer.setContext(contextId);
      composer.setText(`${prior}${prior ? '\n\n' : ''}${pending.text}`);
      added.current.add(pending.id);
      if (added.current.size > 100) added.current.delete(added.current.values().next().value);
    }
    window.parent.postMessage({ channel: 'gab-browser-panel', nonce: panel.nonce, type: 'context-added', id: pending.id }, extensionOrigin);
    setPending(null);
  }, [panel, pathname, pending, extensionOrigin]);
  return null;
}
